import { useState } from "react";
import type { StudentInviteInfo, StudentSummary } from "@edu/shared";
import { CopyLink } from "../shell/CopyLink.tsx";
import { dayCountText } from "../../lib/plurals.ts";
import { TRASH_KEEP_DAYS } from "../homework/trash-keep.ts";
import type { StudentCardState } from "./use-student.ts";

/**
 * Доступ ученика: постоянная ссылка, видимость архива, отзыв.
 *
 * ⚠️ Панель полная и единственная. До 14.09.2026 у карточки было два вида, и
 * окну доставалась вместо панели строка состояния (`AccessLine`) с переходом
 * сюда: код приглашения показывается ровно один раз, а окно закрывается щелчком
 * мимо и по Esc, то есть промах мышью стоил бы перевыпуска ссылки. Окна нет
 * (решение владельца 13.09.2026), делить панель надвое больше не с кем, и
 * состояние ссылки переехало сюда плашкой в заголовок панели: в окне оно было
 * единственным, чего на странице не было.
 */
export function StudentAccess(props: { card: StudentCardState; student: StudentSummary }) {
  const { card, student: s } = props;
  /** Подтверждение своё, а не `confirm()`: нативный диалог блокирует поток. */
  const [asking, setAsking] = useState(false);
  const state = accessPill(s);

  return (
    <section className="card">
      <h2>
        Доступ{" "}
        {/*
          ⚠️ Плашку получает только СОСТОЯНИЕ, которое может измениться, поэтому
          исходов три, а видов два: «ссылки нет» это пустое значение, менять там
          нечему, пока её не выпустили, и пустое в этом продукте говорится
          словами тоном `--muted`.

          ⚠️ «активна» БЕЗ тона, хотя соблазн покрасить её зелёным есть. Зелёный
          занят сданной работой и состоявшимся занятием, то есть тем, что
          случилось хорошо; работающая ссылка это норма, а не достижение. Тот же
          слот в списке учеников читает то же правило, и разъехаться им нечем.
        */}
        {state === null ? (
          <span className="muted stu__count">ссылки нет</span>
        ) : (
          <span className="pill" data-tone={state.tone}>
            {state.text}
          </span>
        )}
      </h2>

      {/*
        ⚠️ Про свой вход сказано ЗДЕСЬ, а не в шапке страницы, где это стояло до
        14.09.2026: вместе с этой строкой сюда переехало всё, что окно карточки
        показывало про доступ, и теперь про ссылку, вход и архив читают в одном
        месте. Слова взяты у подсказки того же слота в списке учеников: два
        текста про один факт расходятся ровно так же, как расходились два вида
        карточки.
      */}
      <p className="muted">
        {inviteText(card.data?.invite ?? null, s.inviteActive)}{" "}
        {!s.unlinked &&
          (s.claimed
            ? "Вход привязан: у ученика свой аккаунт."
            : "Входа нет: ученик ходит по ссылке.")}
      </p>

      {card.link !== null && <CopyLink url={card.link} />}

      <p className="muted">
        Показать код второй раз нельзя: в базе только его хеш. Потерянная ссылка не
        показывается заново, а перевыпускается, и старая при этом перестаёт работать.
      </p>

      <div className="acts">
        <button className="btn" onClick={card.reissue} disabled={card.busy}>
          {s.inviteActive ? "Перевыпустить ссылку" : "Выпустить ссылку"}
        </button>
      </div>

      {/*
        ⚠️ Без «он» и «ему»: половина учеников это девочки, а имени человека
        здесь под рукой нет. Тот же выбор и по той же причине сделан у отчёта о
        пропущенных (`announcements/teacher/skipped-text.ts`).
      */}
      {s.unlinked && (
        <p className="muted">
          Ученик отвязан: новых занятий не завести. Выпустите ссылку заново, и уроки снова
          можно будет создавать.
        </p>
      )}

      {/*
        Тумблер, а не пара кнопок: состояние здесь одно и оно двоичное.
        `role="switch"` вместо чекбокса по той же причине, что в настройках
        комнаты: нативный чекбокс пришлось бы перекрашивать целиком.
      */}
      <button
        type="button"
        className="stu__toggle"
        role="switch"
        aria-checked={s.archiveVisible}
        onClick={card.toggleArchive}
        disabled={card.busy}
      >
        <span>
          {s.archiveVisible ? "Архив открыт" : "Архив закрыт"}
          <span className="stu__hint">
            {s.archiveVisible
              ? "ученик видит доски, свои ответы и переписку с прошлых занятий"
              : "прошлые занятия ученику не открываются"}
          </span>
        </span>
        <span className="stu__switch" aria-hidden="true" />
      </button>

      <p className="muted">
        Переключатель действует сразу, в том числе на уже вошедшего: право на архив
        проверяется на каждый запрос, а не выдаётся вместе с входом.
      </p>

      <hr className="stu__rule" />

      {/*
        Последствия стоят ЗДЕСЬ, до первого нажатия, а не в подтверждении:
        человек должен понимать, что он собирается сделать, ещё когда только
        выбирает кнопку.
      */}
      <p className="muted">Что будет после отзыва:</p>
      <ul className="stu__why">
        <li>ссылка перестанет работать: ни на новом устройстве, ни после выхода;</li>
        <li>новых занятий ученику не завести, пока не отправите ему новую ссылку;</li>
        <li>уроки и доски остаются у вас;</li>
        {/*
          ⚠️ Домашки НЕ ведут себя как уроки, и человек обязан узнать это здесь,
          а не через месяц по факту. Архив уроков после отвязки закрывается
          обратимым переключателем и ничего не удаляет; домашки уходят в корзину
          и через 30 суток удаляются насовсем, вместе со следом для статистики
          навыков. Асимметрия принята владельцем 16.08.2026 (раздел 1.1
          docs/homework-design.md), и там же предписан этот текст.

          Строка «уроки, доски и работы остаются у вас» стояла здесь до фазы 1
          домашки и стала неправдой ровно в тот день, когда домашки появились.
          Это второй такой случай: первый разобран 15.08.2026 в 5.10 CLAUDE.md,
          где предупреждение обещало «ученик потеряет доступ к комнате», чего
          отзыв ссылки не делает.
        */}
        <li>
          домашки уйдут в корзину и через {dayCountText(TRASH_KEEP_DAYS)} удалятся вместе с
          ответами ученика. Новая ссылка вернёт их;
        </li>
        {/*
          ⚠️ Правило рассылки СВОЁ, не как у домашки и не как у уроков: отзыв
          закрывает запись и не закрывает чтение. Отвязанный перестаёт отвечать
          в нитку, которую читают остальные дети группы
          (`addAnnouncementReply`), прошлые сообщения дочитывает, а свои ответы
          удаляет всегда (`deleteAnnouncementReply` пару не проверяет). Это то
          же «дальше не ходит, а не вон отсюда» из раздела 5.10 CLAUDE.md.

          Строка ждала экранов рассылки и дописана вместе с ними: до них
          преподаватель прочитал бы здесь про сообщения, которых он никогда не
          отправлял.
        */}
        <li>
          отвечать на ваши сообщения ученик больше не сможет. Прошлые дочитает, а свои
          ответы сможет удалить;
        </li>
        <li>
          уже открытая у ученика вкладка живёт до конца его сессии. Чтобы закрыть
          прошлые занятия прямо сейчас, выключите архив переключателем выше.
        </li>
      </ul>

      <div className="acts">
        {asking ? (
          <>
            {/*
              Шаг подтверждения: следующее нажатие уже необратимо, и вес это
              показывает заливкой (`is-confirm`), а не только цветом. Отмена
              рядом обязана быть тише подтверждения: пара «подтвердить /
              отменить» одинаковыми прямоугольниками это ровно та ошибка,
              ради которой заведены веса.
            */}
            <button
              className="btn btn--danger is-confirm"
              onClick={() => {
                card.revoke();
                setAsking(false);
              }}
              disabled={card.busy}
            >
              Да, отозвать
            </button>
            <button
              className="btn btn--quiet"
              onClick={() => setAsking(false)}
              disabled={card.busy}
            >
              Отмена
            </button>
          </>
        ) : (
          <button
            className="btn btn--danger"
            onClick={() => setAsking(true)}
            disabled={card.busy || (s.unlinked && !s.inviteActive)}
          >
            Отозвать ссылку
          </button>
        )}
      </div>
    </section>
  );
}

/**
 * Состояние ссылки плашкой, либо `null`, если ссылки ещё не выпускали.
 *
 * Своей функцией, а не веткой в разметке: до 14.09.2026 те же два вида считались
 * ещё и в окне карточки (`AccessLine`), и два экземпляра одного правила это
 * ровно тот способ, которым в продукте расходятся тексты.
 */
function accessPill(s: StudentSummary): { text: string; tone: "warn" | undefined } | null {
  // ⚠️ Слова те же, что в слоте «Ссылка» списка учеников, буква в букву. Артборд
  // системы предлагает вместо «отвязан» слово «отозван», и менять его надо сразу
  // в обоих местах и одним решением: один экран, переименованный в одиночку, это
  // и есть тот разнобой, ради которого всё это переносится
  if (s.unlinked) return { text: "отвязан", tone: "warn" };
  return s.inviteActive ? { text: "активна", tone: undefined } : null;
}

/**
 * Состояние ссылки словами.
 *
 * Читается последняя ссылка пары, а не активная: «не выпускали» и «отозвали» на
 * экране разные сообщения, а по отсутствию активной их не различить.
 */
const dayMonth = new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "long" });

function inviteText(invite: StudentInviteInfo | null, active: boolean): string {
  if (invite === null) return "Ссылку ещё не выпускали.";

  if (!active) {
    const at = invite.revokedAt === null ? null : dayMonth.format(new Date(invite.revokedAt));
    return at === null ? "Активной ссылки нет." : `Ссылка отозвана ${at}.`;
  }

  const issued = dayMonth.format(new Date(invite.issuedAt));
  if (invite.firstUsedAt === null) {
    return `Ссылка выдана ${issued}, ученик её ещё не открывал.`;
  }

  const opened = dayMonth.format(new Date(invite.firstUsedAt));
  // Число входов честнее числа устройств: одно устройство после чистки cookie
  // приходит по ссылке снова, и назвать это «двумя устройствами» было бы враньём
  return `Ссылка выдана ${issued}, погашена ${opened}. Входов по ней: ${invite.useCount}.`;
}
